import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./ui/AppShell";
import { ErrorBoundary, Skeleton } from "./ui/components";
import { LiveProvider } from "./ui/live";
import "./styles.css";

const Home = lazy(() => import("./pages/Home"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Players = lazy(() => import("./pages/Players"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const Teams = lazy(() => import("./pages/Teams"));
const TeamProfile = lazy(() => import("./pages/TeamProfile"));
const Matches = lazy(() => import("./pages/Matches"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventQualifier = lazy(() => import("./pages/EventQualifier"));
const Contact = lazy(() => import("./pages/Contact"));
const Status = lazy(() => import("./pages/Status"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 60_000,
      retry: false
    }
  }
});

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LiveProvider>
          <BrowserRouter>
            <AppShell>
              <Suspense fallback={<main className="mx-auto min-h-screen max-w-7xl px-4 py-8"><Skeleton className="h-96" /></main>}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/players" element={<Players />} />
                  <Route path="/player/:identifier" element={<PlayerProfile />} />
                  <Route path="/player/:identifier/event/:eventSlug" element={<PlayerProfile />} />
                  <Route path="/teams" element={<Teams />} />
                  <Route path="/team/:slug" element={<TeamProfile />} />
                  <Route path="/matches" element={<Matches />} />
                  <Route path="/match/:id" element={<MatchDetail />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/events/:slug" element={<EventDetail />} />
                  <Route path="/events/:slug/qualifier" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifier/game/:gameSlug" element={<EventQualifier />} />
                  <Route path="/events/:slug/games/:gameSlug" element={<EventQualifier />} />
                  <Route path="/events/:slug/games/:gameSlug/matches/:matchId" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/teams" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/teams/:teamSlug" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/players" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/players/:playerIdentifier" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/games" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/games/:gameSlug" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/matches" element={<EventQualifier />} />
                  <Route path="/events/:slug/qualifiers/matches/:matchId" element={<EventQualifier />} />
                  <Route path="/events/:slug/calendar" element={<EventQualifier />} />
                  <Route path="/contacto" element={<Contact />} />
                  <Route path="/status" element={<Status />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/*" element={<Admin />} />
                  <Route path="/404" element={<NotFound />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </Suspense>
            </AppShell>
          </BrowserRouter>
        </LiveProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
